import React, { useState, useContext, useEffect } from "react";
import { AppContext } from "../context/AppContext";
import { CalendarService } from "../utils/apiService";
import {
  Card,
  Calendar,
  Badge,
  Alert,
  Modal,
  Form,
  Input,
  DatePicker,
  TimePicker,
  Select,
  Button,
  Typography,
  Row,
  Col,
  Tooltip,
  Space,
  Tag,
  List,
  Divider,
  message,
} from "antd";
import {
  CalendarOutlined,
  PlusOutlined,
  ClockCircleOutlined,
  UserOutlined,
  BellOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import moment from "moment";
import "./Styles.css";

// Custom theme colors for consistency
const ZAMBIA_THEME = {
  primary: "#078930", // Zambian green
  secondary: "#EF3340", // Zambian red
  accent: "#FFB81C", // Gold accent
  neutral: "#2D2D2D", // Dark neutral
};

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const CalendarPage = () => {
  const { company, user } = useContext(AppContext);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [reminders, setReminders] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deletingReminderId, setDeletingReminderId] = useState(null);

  // Load events from API
  const loadEvents = async () => {
    if (!user?.user_id || !company?.id) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const events = await CalendarService.fetchEvents(token, user.user_id, company.id);
      
      // Transform API data to match component structure
      const transformedEvents = events.map(event => ({
        id: event.id,
        title: event.title,
        description: event.description,
        date: moment(event.event_date, 'YYYY-MM-DD'),
        time: event.time,
        type: event.type || 'meeting',
        priority: event.priority || 'medium',
      }));
      
      setReminders(transformedEvents);
    } catch (error) {
      console.error("Failed to load events:", error);
      message.error("Failed to load calendar events");
    } finally {
      setLoading(false);
    }
  };

  // Load events on component mount and when user/company changes
  useEffect(() => {
    loadEvents();
  }, [user?.user_id, company?.id]);

  // Get reminders for selected date
  const getRemindersForDate = (date) => {
    return reminders.filter(reminder => 
      moment(reminder.date).isSame(date, 'day')
    );
  };

  // Get list data for calendar cells
  const getListData = (value) => {
    const dayReminders = getRemindersForDate(value);
    return dayReminders.map(reminder => ({
      type: getPriorityColor(reminder.priority),
      content: reminder.title,
    }));
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  // Get reminder type icon
  const getReminderIcon = (type) => {
    switch (type) {
      case 'meeting': return <UserOutlined />;
      case 'call': return <ClockCircleOutlined />;
      case 'deadline': return <BellOutlined />;
      default: return <CalendarOutlined />;
    }
  };

  // Date cell render
  const dateCellRender = (value) => {
    const listData = getListData(value);
    return (
      <ul className="events">
        {listData.map((item, index) => (
          <li key={index}>
            <Badge status={item.type} text={item.content} />
          </li>
        ))}
      </ul>
    );
  };

  // Month cell render
  const monthCellRender = (value) => {
    const monthReminders = reminders.filter(reminder => 
      moment(reminder.date).isSame(value, 'month')
    );
    return monthReminders.length > 0 ? (
      <div className="notes-month">
        <section>
          <span>{monthReminders.length} reminders</span>
        </section>
      </div>
    ) : null;
  };

  // Handle date select
  const onDateSelect = (date) => {
    setSelectedDate(date);
  };

  // Handle date change
  const onDateChange = (date) => {
    setSelectedDate(date);
  };

  // Show add reminder modal
  const showAddReminderModal = () => {
    console.log('Add Reminder button clicked');
    console.log('Current isModalVisible:', isModalVisible);
    setEditingReminder(null);
    form.resetFields();
    form.setFieldsValue({
      date: selectedDate,
      time: moment('09:00', 'HH:mm'),
      priority: 'medium',
      type: 'meeting',
    });
    setIsModalVisible(true);
    console.log('Modal should now be visible');
  };

  // Show edit reminder modal
  const showEditReminderModal = (reminder) => {
    setEditingReminder(reminder);
    form.setFieldsValue({
      title: reminder.title,
      description: reminder.description,
      date: moment(reminder.date),
      time: moment(reminder.time, 'HH:mm'),
      priority: reminder.priority,
      type: reminder.type,
    });
    setIsModalVisible(true);
  };

  // Handle modal submit
  const handleModalSubmit = async () => {
    console.log('Modal submit clicked');
    console.log('User:', user);
    console.log('Company:', company);
    
    if (!user?.user_id || !company?.id) {
      console.log('Missing user or company info');
      message.error("User or company information is missing");
      return;
    }

    try {
      const values = await form.validateFields();
      const token = localStorage.getItem("token");
      
      const eventData = {
        title: values.title,
        description: values.description,
        event_date: values.date.format('YYYY-MM-DD'),
        time: values.time.format('HH:mm'),
        priority: values.priority,
        type: values.type,
        user_id: user.user_id,
        company_id: company.id,
      };

      console.log('Event data being sent:', eventData);
      console.log('Token:', token);

      setLoading(true);
      
      if (editingReminder) {
        // Update existing reminder
        console.log('Updating event...');
        await CalendarService.updateEvent(token, editingReminder.id, eventData);
        message.success('Reminder updated successfully');
      } else {
        // Add new reminder
        console.log('Creating new event...');
        const result = await CalendarService.createEvent(token, eventData);
        console.log('Create event result:', result);
        message.success('Reminder added successfully');
      }

      setIsModalVisible(false);
      form.resetFields();
      await loadEvents(); // Reload events
    } catch (error) {
      console.error('Failed to save reminder:', error);
      console.error('Error details:', error.response?.data || error.message);
      if (error.response?.data?.errors) {
        console.error('Specific errors:', error.response.data.errors);
        error.response.data.errors.forEach((err, index) => {
          console.error(`Error ${index + 1}:`, err);
        });
      }
      message.error('Failed to save reminder. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Delete reminder
  const deleteReminder = (reminderId) => {
    console.log('deleteReminder function called with ID:', reminderId);
    setDeletingReminderId(reminderId);
    setDeleteModalVisible(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    try {
      console.log('Delete confirmed for reminder ID:', deletingReminderId);
      const token = localStorage.getItem("token");
      setLoading(true);
      await CalendarService.deleteEvent(token, deletingReminderId);
      message.success('Reminder deleted successfully');
      await loadEvents(); // Reload events
      setDeleteModalVisible(false);
      setDeletingReminderId(null);
    } catch (error) {
      console.error('Failed to delete reminder:', error);
      console.error('Delete error details:', error.response?.data || error.message);
      message.error('Failed to delete reminder. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setDeletingReminderId(null);
  };

  // Get today's reminders
  const todayReminders = getRemindersForDate(moment());
  const selectedDateReminders = getRemindersForDate(selectedDate);

  return (
    <div className="calendar-container">
      {/* Header Card */}
      <Card
        className="welcome-banner"
        style={{
          background: `linear-gradient(135deg, ${ZAMBIA_THEME.primary}40, ${ZAMBIA_THEME.accent}30)`,
          marginBottom: 24,
          borderRadius: 8,
          border: `1px solid ${ZAMBIA_THEME.primary}20`,
        }}
      >
        <Row align="middle" justify="space-between">
          <Col>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <CalendarOutlined 
                style={{ fontSize: 36, color: ZAMBIA_THEME.primary }} 
              />
              <div>
                <Title level={3} style={{ margin: 0, color: ZAMBIA_THEME.neutral }}>
                  Personal Calendar
                </Title>
                <Text>Manage your meetings, calls, and important reminders</Text>
              </div>
            </div>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={showAddReminderModal}
              size="large"
            >
              Add Reminder
            </Button>
          </Col>
        </Row>
      </Card>

      <Row gutter={[24, 24]}>
        {/* Main Calendar */}
        <Col xs={24} lg={16}>
          <Card 
            title="Calendar View" 
            className="calendar-card"
            extra={
              <Space>
                <Tag color={ZAMBIA_THEME.primary}>
                  {selectedDate.format('MMMM YYYY')}
                </Tag>
              </Space>
            }
          >
            <Calendar
              value={selectedDate}
              onSelect={onDateSelect}
              onChange={onDateChange}
              dateCellRender={dateCellRender}
              monthCellRender={monthCellRender}
              className="professional-calendar"
            />
          </Card>
        </Col>

        {/* Sidebar */}
        <Col xs={24} lg={8}>
          {/* Today's Reminders */}
          <Card
            title={
              <Space>
                <BellOutlined style={{ color: ZAMBIA_THEME.primary }} />
                Today's Reminders
              </Space>
            }
            className="reminders-card"
            style={{ marginBottom: 24 }}
          >
            {todayReminders.length > 0 ? (
              <List
                dataSource={todayReminders}
                renderItem={(reminder) => (
                  <List.Item
                    actions={[
                      <Tooltip title="Edit">
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          onClick={() => showEditReminderModal(reminder)}
                        />
                      </Tooltip>,
                      <Tooltip title="Delete">
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            console.log('Delete button clicked for reminder:', reminder.id);
                            deleteReminder(reminder.id);
                          }}
                        />
                      </Tooltip>,
                    ]}
                  >
                    <List.Item.Meta
                      avatar={getReminderIcon(reminder.type)}
                      title={
                        <Space>
                          {reminder.title}
                          <Tag color={getPriorityColor(reminder.priority)}>
                            {reminder.priority}
                          </Tag>
                        </Space>
                      }
                      description={
                        <div>
                          <Text type="secondary">{reminder.time}</Text>
                          <br />
                          <Text>{reminder.description}</Text>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Alert
                message="No reminders for today"
                description="You have a clear schedule today!"
                type="success"
                showIcon
              />
            )}
          </Card>

          {/* Selected Date Reminders */}
          {!selectedDate.isSame(moment(), 'day') && (
            <Card
              title={
                <Space>
                  <CalendarOutlined style={{ color: ZAMBIA_THEME.accent }} />
                  {selectedDate.format('DD MMM YYYY')}
                </Space>
              }
              className="reminders-card"
            >
              {selectedDateReminders.length > 0 ? (
                <List
                  dataSource={selectedDateReminders}
                  renderItem={(reminder) => (
                    <List.Item
                      actions={[
                        <Tooltip title="Edit">
                          <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => showEditReminderModal(reminder)}
                          />
                        </Tooltip>,
                        <Tooltip title="Delete">
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => {
                            console.log('Delete button clicked for reminder:', reminder.id);
                            deleteReminder(reminder.id);
                          }}
                          />
                        </Tooltip>,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={getReminderIcon(reminder.type)}
                        title={
                          <Space>
                            {reminder.title}
                            <Tag color={getPriorityColor(reminder.priority)}>
                              {reminder.priority}
                            </Tag>
                          </Space>
                        }
                        description={
                          <div>
                            <Text type="secondary">{reminder.time}</Text>
                            <br />
                            <Text>{reminder.description}</Text>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              ) : (
                <Alert
                  message="No reminders"
                  description="No reminders scheduled for this date"
                  type="info"
                  showIcon
                />
              )}
            </Card>
          )}
        </Col>
      </Row>

      {/* Add/Edit Reminder Modal */}
      <Modal
        title={editingReminder ? "Edit Reminder" : "Add New Reminder"}
        open={isModalVisible}
        onOk={handleModalSubmit}
        onCancel={() => setIsModalVisible(false)}
        width={600}
        okText={editingReminder ? "Update" : "Add"}
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            priority: 'medium',
            type: 'meeting',
          }}
        >
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Please enter a title' }]}
          >
            <Input placeholder="Enter reminder title" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea 
              rows={3} 
              placeholder="Enter reminder description (optional)" 
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="date"
                label="Date"
                rules={[{ required: true, message: 'Please select a date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="time"
                label="Time"
                rules={[{ required: true, message: 'Please select a time' }]}
              >
                <TimePicker 
                  style={{ width: '100%' }} 
                  format="HH:mm"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Type"
                rules={[{ required: true, message: 'Please select a type' }]}
              >
                <Select>
                  <Option value="meeting">Meeting</Option>
                  <Option value="call">Call</Option>
                  <Option value="deadline">Deadline</Option>
                  <Option value="personal">Personal</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="priority"
                label="Priority"
                rules={[{ required: true, message: 'Please select a priority' }]}
              >
                <Select>
                  <Option value="high">High</Option>
                  <Option value="medium">Medium</Option>
                  <Option value="low">Low</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Reminder"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Delete"
        okType="danger"
        cancelText="Cancel"
        confirmLoading={loading}
      >
        <p>Are you sure you want to delete this reminder?</p>
      </Modal>

      <style jsx>{`
        .calendar-container .professional-calendar {
          .ant-picker-calendar-header {
            padding: 8px 0;
          }
          
          .ant-picker-calendar-date-content {
            height: 60px;
          }
          
          .events {
            list-style: none;
            margin: 0;
            padding: 0;
          }
          
          .events li {
            margin: 2px 0;
            font-size: 12px;
          }
          
          .ant-badge-status-dot {
            width: 6px;
            height: 6px;
          }
          
          .notes-month {
            text-align: center;
            font-size: 12px;
            color: ${ZAMBIA_THEME.primary};
          }
        }
        
        .reminders-card .ant-list-item {
          padding: 12px 0;
        }
        
        .reminders-card .ant-list-item-meta-avatar {
          color: ${ZAMBIA_THEME.primary};
        }
      `}</style>
    </div>
  );
};

export default CalendarPage;